import type { FetchPolicy } from '@apollo/client';
import type { ApolloClient } from '@frameio/graphql-client';
import type {
  AssetForDownloadFragment,
  FolderChildrenForDownloadFragment,
  PageInput,
} from '@frameio/graphql-client/generated';
import {
  GetAssetChildrenForDownload,
  GetAssetsForDownload,
  GetShareAssetChildrenForDownload,
  GetShareAssetsForDownload,
} from '@frameio/graphql-client/generated';

export type AssetsForDownload = AssetForDownloadFragment[] | undefined;

export type FolderChildrenForDownload =
  FolderChildrenForDownloadFragment['matchingChildren'];

export type DownloadableAsset =
  | NonNullable<AssetsForDownload>[number]
  | NonNullable<FolderChildrenForDownload>['nodes'][number];

type DownloadScope = {
  accountId?: string;
  shareId?: string;
};

export async function fetchAssetsForDownload(
  client: ApolloClient<any>,
  { accountId, shareId }: DownloadScope,
  assetIds: string[],
  fetchPolicy: FetchPolicy
): Promise<AssetsForDownload> {
  if (accountId) {
    const result = await client.query({
      fetchPolicy,
      query: GetAssetsForDownload,
      variables: { accountId, assetIds, first: assetIds.length },
    });

    return result.data.account?.assets?.nodes;
  }

  // Share pages have no active account; share reviewers may not be account members.
  if (shareId) {
    const result = await client.query({
      fetchPolicy,
      query: GetShareAssetsForDownload,
      variables: { shareId, assetIds, first: assetIds.length },
    });

    return result.data.share?.assets?.nodes;
  }

  return undefined;
}

/**
 * Fetches one page of a folder's children. Share reviewers may not be members
 * of the share's account, so share downloads resolve the folder through the
 * share rather than its account.
 */
export async function fetchFolderChildrenForDownload(
  client: ApolloClient<any>,
  { shareId }: Pick<DownloadScope, 'shareId'>,
  folderId: string,
  page: PageInput
): Promise<FolderChildrenForDownload | undefined> {
  // Network-only: download URLs are signed, so fetch fresh ones.
  if (shareId) {
    const result = await client.query({
      fetchPolicy: 'network-only',
      query: GetShareAssetChildrenForDownload,
      variables: { shareId, assetId: folderId, page },
    });
    const folder = result.data.share?.asset;

    return folder && 'matchingChildren' in folder
      ? folder.matchingChildren
      : undefined;
  }

  const result = await client.query({
    fetchPolicy: 'network-only',
    query: GetAssetChildrenForDownload,
    variables: { assetId: folderId, page },
  });
  const folder = result.data.account?.asset;

  return folder && 'matchingChildren' in folder
    ? folder.matchingChildren
    : undefined;
}
